<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country selection and onboarding type -->
    <div v-if="shownStep.step_name === StepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationAccountFlow"
        :addNewDeviceFlow="addNewDeviceFlow"
        :existingCompanies="existingCompanies"
        :countryLocale="locale"
        :enableNewApplicationFlow="false"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
      />
    </div>

    <!-- STEP 2: Contact review -->
    <div v-if="shownStep.step_name === StepName.CONTACT_REVIEW">
      <template v-if="companyIndex !== -1">
        <EsContactReviewConfigurationComponent
          v-if="country === 'ES'"
          :key="companyIndex"
          :accountData="accountData"
          :companyIndex="companyIndex"
          @handleUpdateCompany="signupStore.updateCompany"
          @updateAccountData="signupStore.updateAccount"
          @selectExistingCompany="handleSelectExistingCompany"
          @resetCompanySelection="handleResetCompanySelection"
          class="mb-4"
        />
        <PlContactReviewConfigurationComponent
          v-else
          :key="companyIndex"
          :accountData="accountData"
          :companyIndex="companyIndex"
          @handleUpdateCompany="signupStore.updateCompany"
          @updateAccountData="signupStore.updateAccount"
          @selectExistingCompany="handleSelectExistingCompany"
          @resetCompanySelection="handleResetCompanySelection"
          class="mb-4"
        />
      </template>
    </div>

    <!-- STEP 3: Contract review -->
    <template
      v-if="
        shownStep.step_name === StepName.CONTRACT_REVIEW && companyIndex !== -1
      "
    >
      <EsContractReviewConfigurationComponent
        v-if="country === 'ES'"
        :accountData="accountData"
        @createContainerForStartSigningReady="
          storeCreateContainerForStartSigning
        "
        @disableChangingInformation="handleDisableChangingInformation"
        class="mb-4"
      />
      <PlContractReviewConfigurationComponent
        v-else
        :accountData="accountData"
        @createContainerForStartSigningReady="
          storeCreateContainerForStartSigning
        "
        @disableChangingInformation="handleDisableChangingInformation"
        class="mb-4"
      />
    </template>

    <!-- Buttons -->
    <div
      v-if="signupStep > 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
</template>

<script setup lang="ts">
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useQueryManager } from "@/composables/useQueryManager";
import { StepConfig, useStepFunctions } from "@/composables/useStepFunctions";
import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import EsContactReviewConfigurationComponent from "@/signup/es/ContactReviewConfigurationComponent.vue";
import EsContractReviewConfigurationComponent from "@/signup/es/ContractReviewConfigurationComponent.vue";
import PlContactReviewConfigurationComponent from "@/signup/pl/ContactReviewConfigurationComponent.vue";
import PlContractReviewConfigurationComponent from "@/signup/pl/ContractReviewConfigurationComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

enum StepName {
  CHOOSE_ONBOARDING,
  CONTACT_REVIEW,
  CONTRACT_REVIEW,
}

/**
 * The ES and PL signup offers only the authorization path — the producer authorizes Soldera to
 * manage the Guarantees of Origin they already receive — so it has no new-device steps. Only the
 * step contents differ per country. ProducerSignupView keys it on the country, so a switch between
 * the two remounts it.
 */
const props = defineProps<{
  country: "ES" | "PL";
}>();

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const { account: accountData, companyIndex } = storeToRefs(signupStore);
const { updateQuery, getQueryValue } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);

const { postStepCallback } = useSignupStepSave(isLoading);
const { t } = useI18n();

const locale = computed(() => props.country.toLowerCase());

const existingCompanies = computed(() => {
  return accountData.value.companies.filter(
    (company) =>
      company.id !== undefined &&
      company.country === props.country &&
      company.documents.length > 0
  );
});

const STEP_CONFIG = computed((): Record<StepName, StepConfig> => ({
  [StepName.CHOOSE_ONBOARDING]: {
    step_name: StepName.CHOOSE_ONBOARDING,
    title: t(`signup.navsteps.${locale.value}.step1`),
    titleKey: `signup.navsteps.${locale.value}.step1`,
  },
  [StepName.CONTACT_REVIEW]: {
    step_name: StepName.CONTACT_REVIEW,
    title: t(`signup.navsteps.${locale.value}.step2`),
    titleKey: `signup.navsteps.${locale.value}.step2`,
    apiEndpoint: `/api/${locale.value}/update-company`,
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      signupStore.createCompanyIfMissing();
      return true;
    },
  },
  [StepName.CONTRACT_REVIEW]: {
    step_name: StepName.CONTRACT_REVIEW,
    title: t(`signup.navsteps.${locale.value}.step3`),
    titleKey: `signup.navsteps.${locale.value}.step3`,
    postStepCallback: postStepCallback,
  },
}));

const flowSteps = computed(() => {
  return [
    STEP_CONFIG.value[StepName.CHOOSE_ONBOARDING],
    STEP_CONFIG.value[StepName.CONTACT_REVIEW],
    STEP_CONFIG.value[StepName.CONTRACT_REVIEW],
  ];
});

const { shownStep, setStep, navSteps, invokePreStepCallback } =
  useStepFunctions(flowSteps, signupStep);

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const changingInformationDisabled = ref(false);

const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

const createAuthorizationAccountFlow = async () => {
  await updateQuery({ addAuthorizationAgreement: "true" });
  setStep(2);
};

const addNewDeviceFlow = () => {};

const handleSelectExistingCompany = async (companyId: number) => {
  isLoading.value = true;
  try {
    await updateQuery({
      company_id: companyId.toString(),
      step: "2",
    });
    signupStore.removeEmptyCompanyIfExists();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const handleResetCompanySelection = () => {
  updateQuery({
    company_id: undefined,
    generator_id: undefined,
    step: "1",
  });
  signupStore.removeEmptyCompanyIfExists();
};

onMounted(async () => {
  await invokePreStepCallback();
  isLoading.value = false;
});
</script>
